import { Box } from "@dropbox/dig-foundations";
import { getDemoUrl } from "@mirage/growth/onboarding/assistant-demo/demo-contents";
import { useDemoQuerySuggestions } from "@mirage/growth/onboarding/hooks/useDemoQuerySuggestions";
import {
  type AskFromSuggestionParams,
  CtaSource,
} from "@mirage/service-conversation/router_state_types";
import { buildConversationRouterState } from "@mirage/service-conversation/utils/buildConversationRouterState";
import { useNavigate } from "react-router-dom";
import { SuggestionTile } from "../SuggestionTile/SuggestionTile";
import styles from "./QuerySuggestions.module.css";
import { useQuerySuggestions } from "./useQuerySuggestions";

import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_ChatSuggestion } from "@mirage/analytics/events/types/click_chat_suggestion";
import { PAP_Click_DemoQuerySuggestion } from "@mirage/analytics/events/types/click_demo_query_suggestion";
import type { DemoFileName } from "@mirage/growth/onboarding/assistant-demo/demo-contents";
import type { DemoPromptSuggestion } from "@mirage/growth/onboarding/hooks/useDemoQuerySuggestions";
import type { QuerySuggestionResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import { RoutePath } from "@mirage/webapp/routeTypes";
import classNames from "classnames";

const isDemoPrompt = (
  suggestion: QuerySuggestionResult,
): suggestion is DemoPromptSuggestion => {
  return (suggestion as DemoPromptSuggestion).promptType !== undefined;
};

export function QuerySuggestions() {
  const navigate = useNavigate();
  const { reportPapEvent } = useMirageAnalyticsContext();
  const { demoSuggestions } = useDemoQuerySuggestions();
  const isMobileSize = useIsMobileSize();

  const { isLoading, querySuggestions } = useQuerySuggestions({
    options: { source: "dash" },
    maxSuggestions: 3,
    demoSuggestions,
  });

  function handleSuggestionClick(
    suggestion: QuerySuggestionResult | DemoPromptSuggestion,
  ) {
    reportPapEvent(
      PAP_Click_ChatSuggestion({
        actionSurfaceComponent: "prompt_suggestion",
        generatedQueryString: suggestion.text,
        promptType: "chat_suggestion",
        predictionId: suggestion.predictionId,
        featureLine: "assist",
      }),
    );

    if (isDemoPrompt(suggestion) && suggestion.docIds?.length) {
      const fileName = suggestion.docIds[0] as DemoFileName;
      const prompt = suggestion.promptType;
      const uri = getDemoUrl({ fileName, prompt });
      reportPapEvent(
        PAP_Click_DemoQuerySuggestion({
          actionSurfaceComponent: "prompt_suggestion",
          promptType: "chat_suggestion",
          dashDemoQuerySuggestionType: suggestion.promptType,
          featureLine: "assist",
        }),
      );
      navigate(uri);
    } else {
      const askConversationParams: AskFromSuggestionParams = {
        message: suggestion.text!,
        attachments: suggestion.docIds || [],
        agentIds: suggestion.agentIds || [],
      };
      const routerState = buildConversationRouterState(
        CtaSource.OneClickAskPrompt,
        askConversationParams,
      );
      navigate(RoutePath.CHAT, { state: routerState });
    }
  }

  if (!querySuggestions.length) return null;

  return (
    <Box
      className={classNames(styles.querySuggestionsContainer, {
        [styles.isMobile]: isMobileSize,
      })}
    >
      {querySuggestions.map((suggestion, index) => (
        <SuggestionTile
          key={index}
          text={suggestion.text}
          onClick={() => handleSuggestionClick(suggestion)}
          isLoading={isLoading}
          withPadding={false}
          predictionId={suggestion.predictionId}
        />
      ))}
    </Box>
  );
}
